'use client';

import {useId, useRef, useState, type KeyboardEvent, type ReactNode} from 'react';
import {track} from '@/lib/analytics';

type Props = {
  titles: string[];
  panels: ReactNode[];
  /** Accessible name of the tab list. */
  label?: string;
  trackingLabel?: string;
};

/**
 * Accessible tabs (WAI-ARIA tabs pattern): arrow keys / Home / End move between tabs, only the active tab
 * is in the tab order, tabs and panels are linked via aria-controls / aria-labelledby.
 * All panels are server-rendered and stay in the DOM (hidden when inactive).
 */
const Tabs = ({titles, panels, label = 'Details', trackingLabel}: Props) => {
  const [active, setActive] = useState(0);
  const id = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    setActive(index);
    tabRefs.current[index]?.focus();
    track('view_product_tab', {product: trackingLabel ?? '', tab: titles[index]});
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const last = titles.length - 1;
    const next = {ArrowRight: active === last ? 0 : active + 1, ArrowLeft: active === 0 ? last : active - 1, Home: 0, End: last}[
      event.key
    ];
    if (next !== undefined) {
      event.preventDefault();
      select(next);
    }
  };

  return (
    <div className='flex w-full flex-col'>
      <div
        role='tablist'
        aria-label={label}
        onKeyDown={onKeyDown}
        className='mb-8 flex gap-2 overflow-x-auto rounded-full bg-zinc-100 p-1.5'>
        {titles.map((title, index) => (
          <button
            key={index}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            type='button'
            role='tab'
            id={`${id}-tab-${index}`}
            aria-selected={active === index}
            aria-controls={`${id}-panel-${index}`}
            tabIndex={active === index ? 0 : -1}
            onClick={() => select(index)}
            className={`shrink-0 cursor-pointer rounded-full px-5 py-2.5 text-sm font-semibold transition ${
              active === index ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
            }`}>
            {title}
          </button>
        ))}
      </div>
      {panels.map((panel, index) => (
        <div
          key={index}
          role='tabpanel'
          id={`${id}-panel-${index}`}
          aria-labelledby={`${id}-tab-${index}`}
          tabIndex={0}
          hidden={active !== index}
          className='flex flex-col rounded-lg focus-visible:outline-2'>
          {panel}
        </div>
      ))}
    </div>
  );
};

export default Tabs;
